import gsap from 'gsap';
import { Container, Sprite, Text } from 'pixi.js';
import { Signal } from 'typed-signals';

import { Shared } from '../App';
import { Game } from '../game/Game';
import { StrUtils } from '../utils/StrUtils';
import { Widget } from './Widget';

export enum ValueWidgetType
    {
    COINS,
    RACE_COINS,
    RACE_TIME,
    DIAMONDS,
}

export class ValueWidget extends Widget
{
    public game: Game;
    public content: Container;
    public base: Sprite;
    public icon: Sprite;
    public label: Text;
    public type: ValueWidgetType;
    public valueUpdated: Signal<(val: number) => void>;
    private _valueCache: number;

    constructor(game: Game, type: ValueWidgetType)
    {
        super();
        this.game = game;
        this.type = type;

        this.content = new Container();
        this.view.addChild(this.content);

        this.base = Sprite.from(Shared.assets.getTexture('tt-valueholder-large'));
        this.base.anchor.set(0.5);
        this.content.addChild(this.base);

        this.icon = new Sprite();
        this.icon.anchor.set(0.5);
        this.content.addChild(this.icon);
        this.icon.x = (-this.base.width * 0.5) + 5;

        this.valueUpdated = new Signal<(val: number) => void>();

        this.label = new Text('', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'left',
            fontSize: 20,
            stroke: 0x333333,
            strokeThickness: 7,
            lineJoin: 'round',
        });
        this.label.resolution = 2;
        this.label.y = -2;

        this.content.addChild(this.label);

        if (this.type === ValueWidgetType.COINS || this.type === ValueWidgetType.RACE_COINS)
        {
            this.icon.texture = Shared.assets.getTexture('coins-small');
            this.label.anchor.set(0.5);
            this.label.x = 5;
        }
        if (this.type === ValueWidgetType.RACE_TIME)
        {
            this.icon.texture = Shared.assets.getTexture('icon-timer');
            this.label.anchor.set(0, 0.5);
            this.label.x = -22;
        }
        if (this.type === ValueWidgetType.DIAMONDS)
        {
            this.icon.texture = Shared.assets.getTexture('diamond-small');
            this.label.anchor.set(0.5);
            this.label.x = 5;
        }

        this.icon.height = this.base.height * 1.35;
        this.icon.scale.x = this.icon.scale.y;
        this._valueCache = -1;
    }

    protected async onOpen(): Promise<void>
    {
        this._valueCache = -1;
        this.refresh();
        gsap.killTweensOf(this.content);
        this.content.x = 300;
        await gsap.to(this.content, { x: 0, duration: 0.2, ease: 'expo.out' });
    }

    protected async onClose(): Promise<void>
    {
        this.refresh();
        gsap.killTweensOf(this.content);
        await gsap.to(this.content, { x: 300, duration: 0.2, ease: 'expo.in' });
    }

    public refresh(): void
    {
        if (!this._opened) return;

        if (this.type === ValueWidgetType.RACE_COINS)
        {
            if (this._valueCache !== this.game.race.coins)
            {
                this._valueCache = this.game.race.coins;
                this.label.text = `${this.game.race.coins}`;
            }
        }
        if (this.type === ValueWidgetType.RACE_TIME)
        {
            if (this._valueCache !== this.game.race.time)
            {
                this._valueCache = this.game.race.time;
                this.label.text = StrUtils.formatRaceTime(this.game.race.time);
            }
        }
        if (this.type === ValueWidgetType.COINS)
        {
            if (this._valueCache !== this.game.app.user.data.coins)
            {
                this._valueCache = this.game.app.user.data.coins;
                this.label.text = `${this.game.app.user.data.coins}`;
                this.valueUpdated.emit(this.game.app.user.data.coins);
            }
        }
        if (this.type === ValueWidgetType.DIAMONDS)
        {
            if (this._valueCache !== this.game.app.user.data.diamonds)
            {
                this._valueCache = this.game.app.user.data.diamonds;
                this.label.text = `${this.game.app.user.data.diamonds}`;
                this.valueUpdated.emit(this.game.app.user.data.diamonds);
            }
        }
    }

    public async wobble(): Promise<void>
    {
        gsap.killTweensOf(this.content);
        gsap.to(this.content.scale, { x: 0.9, y: 1.3, duration: 0.1 });
        await gsap.to(this.content.scale, { x: 1, y: 1, duration: 1, ease: 'elastic.out', delay: 0.1 });
    }
}
